/**
 * First-run / no-real-BoM landing for the Equipment (BOM) tab.
 * Visual language matches DocumentsUploadLandingCard and SedRebuildLandingCard.
 */
import React from 'react';
import { FileSpreadsheet, FileUp, LayoutGrid, Package, PenLine, Sparkles } from 'lucide-react';

export interface SupplyChainLandingCardProps {
  isUploadingBom?: boolean;
  isUploadingQuote?: boolean;
  onUploadBom?: () => void;
  onUploadQuote?: () => void;
  onAddManualItem?: () => void;
  /** Layout → BOM live-link: bootstrap the plan with site-works quantities
   *  from the promoted generation layout. Undefined until a scenario is
   *  promoted in Generation & Impact. */
  onSyncSiteWorksFromLayout?: () => void | Promise<void>;
}

export const SupplyChainLandingCard: React.FC<SupplyChainLandingCardProps> = ({
  isUploadingBom = false,
  isUploadingQuote = false,
  onUploadBom,
  onUploadQuote,
  onAddManualItem,
  onSyncSiteWorksFromLayout,
}) => {
  const busy = isUploadingBom || isUploadingQuote;

  return (
    <div
      className="rounded-md border border-border bg-surface-1/40 p-8 max-w-2xl mx-auto text-center space-y-6"
      data-testid="supply-chain-landing"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
        <Package className="w-7 h-7 text-primary" aria-hidden />
      </div>
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          Build your supply chain plan
        </h3>
        <p className="text-sm text-text-muted leading-relaxed max-w-md mx-auto">
          Import your bill of materials, vendor quotes, or add line items manually.
        </p>
      </div>
      <ul className="text-left text-[11px] text-text-secondary space-y-2 max-w-sm mx-auto">
        <li className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>BoM PDF or Excel equipment schedules — parsed into line items for review</span>
        </li>
        <li className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>Vendor quote PDFs or spreadsheets — linked to BoM rows when matched</span>
        </li>
        <li className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>Lead-time critical path, supplier shortlists, and quote tracking once data is in</span>
        </li>
      </ul>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {onUploadBom && (
          <button
            type="button"
            onClick={onUploadBom}
            disabled={busy}
            title="Upload a bill of materials or equipment schedule (PDF, Excel, CSV)"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="supply-chain-landing-upload-bom"
          >
            <FileUp className={`w-4 h-4 ${isUploadingBom ? 'animate-pulse' : ''}`} aria-hidden />
            {isUploadingBom ? 'Extracting…' : 'Upload BoM'}
          </button>
        )}
        {onUploadQuote && (
          <button
            type="button"
            onClick={onUploadQuote}
            disabled={busy}
            title="Upload a vendor quote PDF or spreadsheet"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="supply-chain-landing-upload-quote"
          >
            <FileSpreadsheet className={`w-4 h-4 ${isUploadingQuote ? 'animate-pulse' : ''}`} aria-hidden />
            {isUploadingQuote ? 'Processing…' : 'Upload quote'}
          </button>
        )}
        {onAddManualItem && (
          <button
            type="button"
            onClick={onAddManualItem}
            disabled={busy}
            title="Add a BoM line item manually"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest border border-border text-text-secondary hover:text-text-primary hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="supply-chain-landing-add-manual"
          >
            <PenLine className="w-4 h-4" aria-hidden />
            Add item manually
          </button>
        )}
        {onSyncSiteWorksFromLayout && (
          <button
            type="button"
            onClick={() => { void onSyncSiteWorksFromLayout(); }}
            disabled={busy}
            title="Bootstrap the plan with screening site-works quantities (fencing, pads, roads, clearing) from the promoted generation layout"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest border border-border text-text-secondary hover:text-text-primary hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="supply-chain-landing-sync-siteworks"
          >
            <LayoutGrid className="w-4 h-4" aria-hidden />
            Sync site-works from layout
          </button>
        )}
      </div>
    </div>
  );
};
